// ---------------------------------------------------------------------------
// ai-features.status.ts — shipping status for each advertised AI capability
// ---------------------------------------------------------------------------
//
// Why this file exists.
//
// /ai-features advertised 25 AI capabilities as though all 25 were running. At
// the time of writing 2 call a model; the rest answer 501 in production. A
// prospect reading the page and a property using the product saw different
// products.
//
// Publishing a roadmap is legitimate. Publishing a roadmap as though it were
// shipped is not. Every feature card now carries one of these three states.
//
// KEYED BY POSITION, not by title, and NOT localized.
//
// The page ships in 9 languages and every feature title is translated, so a
// title-keyed map would only ever match English. Shipping status is a fact
// about the software rather than copy, so it must not vary per language
// either. `[categoryIndex][featureIndex]` is the same in every locale because
// each locale is a translation of one ordered list.
//
// That coupling is real and would break silently if a locale gained or lost a
// feature, so `ai-features.status.test.ts` asserts every locale has the same
// category and feature counts as this map. Reordering or adding a feature in
// one language fails the build rather than mislabelling a card.
// ---------------------------------------------------------------------------

/**
 * live     — runs in production against real tenant data. Usable today.
 * building — the screen exists and the table is populated; the capability is
 *            being wired. Answers 501 today.
 * planned  — on the roadmap, blocked on something named, not started.
 */
export type AIFeatureStatus = 'live' | 'building' | 'planned';

/**
 * Status per [category][feature], matching the order in
 * `ai-features.content.ts`. English titles are comments only — they are here
 * so the map is readable, not so it can be looked up by name.
 */
export const AI_FEATURE_STATUS: readonly (readonly AIFeatureStatus[])[] = [
  // 0 — Revenue Intelligence
  [
    'building', // Dynamic Pricing Engine       — reservations + rate_plans populated
    'building', // Demand Forecasting           — statistics, not an LLM
    'planned',  // Competitor Rate Monitor      — needs a paid rate-shopping feed
    'planned',  // Autonomous Rate Management   — needs Dynamic Pricing to ship first
    'building', // Booking Pattern Discovery    — statistics over reservations
  ],
  // 1 — Guest Experience
  [
    'building', // Guest Concierge Chatbot
    'planned',  // Voice AI Concierge           — needs the telephony stack
    'live',     // Review Response Generator    — AI-3: reads guest_reviews, persists management_response
    'building', // Smart Upsell Engine
    'planned',  // Sentiment Analysis           — needs OTA review ingestion
    'building', // Stay Recommendations
    'building', // Guest Profiling 360
    'live',     // Smart Reply Suggestions      — AI-14: reads chat_conversations, drafts replies
  ],
  // 2 — Operations
  [
    'building', // Automated Night Audit        — statistics over folio + audit log
    'building', // Smart Housekeeping           — assignment optimisation, not an LLM
    'building', // Staff Scheduling             — optimisation, not an LLM
    'planned',  // AI Operations Agent          — unsupervised action reaching a real guest
  ],
  // 3 — Facilities
  [
    'building', // Predictive Maintenance       — statistics over work-order history
    'planned',  // Energy Optimisation          — no meter data; needs per-property IoT
  ],
  // 4 — Compliance & Risk
  [
    'building', // Compliance Automation
    'building', // Anomaly Detection
  ],
  // 5 — Loyalty & Portfolio
  [
    'building', // Loyalty Optimisation
    'building', // Cross-Property Intelligence
    'planned',  // Digital Twin Dashboard       — cost far exceeds buyer relevance
    'planned',  // AI Travel Agent (MCP)        — speculative until agent bookings are real
  ],
];

/** Capabilities usable today — the only figure the hero may present as shipped. */
export function liveFeatureCount(): number {
  return AI_FEATURE_STATUS.flat().filter((s) => s === 'live').length;
}

/** Everything on the page, shipped or not. */
export function totalFeatureCount(): number {
  return AI_FEATURE_STATUS.flat().length;
}

/** Safe lookup — an out-of-range position yields no badge rather than throwing. */
export function featureStatusAt(
  categoryIndex: number,
  featureIndex: number,
): AIFeatureStatus | undefined {
  return AI_FEATURE_STATUS[categoryIndex]?.[featureIndex];
}
